import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import FollowButton from "@/components/FollowButton";
import ChallengePlayerModal from "@/components/ChallengePlayerModal";
import { 
  Search, Star, Trophy, Users, Gamepad2, Clock, 
  Filter, ChevronDown, Loader2, TrendingUp, Award, Swords
} from "lucide-react";

const GAME_TYPES = ["All", "Spades", "Whist", "Backgammon", "Checkers", "Chess", "Dominoes"];
const SKILL_LEVELS = ["All", "Beginner", "Intermediate", "Advanced", "Expert"];
const SORT_OPTIONS = [
  { value: "rating", label: "Top Rated", icon: Star },
  { value: "followers", label: "Most Followed", icon: Users },
  { value: "games_played", label: "Most Active", icon: Gamepad2 },
  { value: "recent", label: "Recently Active", icon: Clock }
];

// Helper to check if player is recently active
const isRecentlyActive = (lastActivity) => {
  if (!lastActivity) return false;
  const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
  return new Date(lastActivity).getTime() > twentyFourHoursAgo;
};

// Player Card Component
const PlayerCard = ({ player, showRank = false, rank = 0, onViewProfile, user, token, onFollowChange, onChallenge }) => (
  <Card 
    className="bg-[#0A0A0A] border-white/10 hover:border-[#7a6200]/50 transition-all cursor-pointer group"
    onClick={() => onViewProfile(player.id)}
    data-testid={`player-card-${player.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        {/* Rank Badge (for top rated) */}
        {showRank && (
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bebas text-lg ${
            rank === 1 ? "bg-yellow-500 text-black" :
            rank === 2 ? "bg-gray-300 text-black" :
            rank === 3 ? "bg-amber-700 text-white" :
            "bg-white/10 text-white"
          }`}>
            {rank}
          </div>
        )}
        
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/20 group-hover:border-[#7a6200]/50 transition-colors">
            {player.avatar ? (
              <img src={player.avatar} alt={player.full_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                <span className="text-[#7a6200] font-bebas text-xl">
                  {player.full_name?.charAt(0) || "?"}
                </span>
              </div>
            )}
          </div>
          {/* Online indicator (if recently active) */}
          {isRecentlyActive(player.last_activity) && (
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-[#0A0A0A]" />
          )}
        </div>

        {/* Player Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bebas text-lg tracking-wider text-white truncate group-hover:text-[#7a6200] transition-colors">
              {player.full_name}
            </h3>
            {player.rating >= 4.5 && (
              <Award className="w-4 h-4 text-[#7a6200] flex-shrink-0" />
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-1">
            {/* Rating */}
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-[#C6A300] fill-current" />
              <span className="text-[#C6A300] text-sm font-ubuntu">
                {player.rating || 0}
              </span>
              {player.rating_count > 0 && (
                <span className="text-[#52525B] text-xs">({player.rating_count})</span>
              )}
            </div>
            
            {/* Skill Level */}
            <span className={`text-xs font-ubuntu capitalize px-2 py-0.5 rounded ${
              player.skill_level === "expert" ? "bg-purple-500/20 text-purple-400" :
              player.skill_level === "advanced" ? "bg-red-500/20 text-red-400" :
              player.skill_level === "intermediate" ? "bg-yellow-500/20 text-yellow-400" :
              "bg-green-500/20 text-green-400"
            }`}>
              {player.skill_level || "beginner"}
            </span>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-4 mt-2 text-[#52525B] text-xs font-ubuntu">
            <div className="flex items-center gap-1">
              <Gamepad2 className="w-3 h-3" />
              <span>{player.games_played || 0} games</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span>{player.followers || 0} followers</span>
            </div>
          </div>

          {/* Preferred Games */}
          {player.preferred_games && player.preferred_games.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {player.preferred_games.slice(0, 3).map((game, i) => (
                <span 
                  key={i}
                  className="text-xs px-2 py-0.5 rounded bg-white/5 text-[#A1A1AA]"
                >
                  {game}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {user && user.id !== player.id && (
          <div className="flex flex-col gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <FollowButton 
              userId={player.id} 
              token={token}
              onFollowChange={onFollowChange}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChallenge(player)}
              className="border-[#7a6200]/50 text-[#7a6200] hover:bg-[#7a6200]/10"
              data-testid={`challenge-btn-${player.id}`}
            >
              <Swords className="w-4 h-4 mr-1" />
              Challenge
            </Button>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Small Player Card Component
const SmallPlayerCard = ({ player, showActivity = false, onViewProfile }) => (
  <div 
    className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors flex-1"
    onClick={() => onViewProfile(player.id)}
  >
    <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20">
      {player.avatar ? (
        <img src={player.avatar} alt={player.full_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
          <span className="text-[#7a6200] font-bebas">{player.full_name?.charAt(0) || "?"}</span>
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-ubuntu text-sm truncate">{player.full_name}</p>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 text-[#C6A300] fill-current" />
          <span className="text-[#C6A300] text-xs">{player.rating || 0}</span>
        </div>
        {showActivity && player.last_activity && (
          <span className="text-[#52525B] text-xs">
            {new Date(player.last_activity).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  </div>
);

const DiscoverPlayersPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [recentlyActive, setRecentlyActive] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [gameType, setGameType] = useState("All");
  const [skillLevel, setSkillLevel] = useState("All");
  const [sortBy, setSortBy] = useState("rating");
  const [showFilters, setShowFilters] = useState(false);
  
  // Challenge modal
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);

  const handleChallenge = (player) => {
    if (!token) {
      toast.error("Please log in to challenge players");
      return;
    }
    setSelectedOpponent(player);
    setChallengeModalOpen(true);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchPlayers();
    }
  }, [gameType, skillLevel, sortBy]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [playersRes, topRatedRes, recentRes] = await Promise.all([
        axios.get(`${API}/players/discover?limit=20`),
        axios.get(`${API}/players/top-rated?limit=5`),
        axios.get(`${API}/players/recently-active?limit=5`)
      ]);
      
      setPlayers(playersRes.data.players || []);
      setTopRated(topRatedRes.data.players || []);
      setRecentlyActive(recentRes.data.players || []);
    } catch (err) {
      console.error("Error fetching players:", err);
      toast.error("Failed to load players");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const params = new URLSearchParams();
      if (gameType !== "All") params.append("game_type", gameType);
      if (skillLevel !== "All") params.append("skill_level", skillLevel);
      params.append("sort_by", sortBy);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/players/discover?${params.toString()}`);
      setPlayers(response.data.players || []);
    } catch (err) {
      console.error("Error fetching players:", err);
    }
  };

  const filteredPlayers = players.filter(player => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      player.full_name?.toLowerCase().includes(query) ||
      player.preferred_games?.some(g => g.toLowerCase().includes(query))
    );
  });

  const handleViewProfile = (playerId) => {
    navigate(`/profile/${playerId}`);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="discover-players-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
              DISCOVER PLAYERS
            </h1>
            <p className="text-[#A1A1AA] font-ubuntu text-lg mt-2">
              Find and connect with skilled players in your favorite games
            </p>
          </div>

          {/* Search and Filters */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search players by name or game..."
                  className="input-dark pl-10 h-12"
                  data-testid="search-players-input"
                />
              </div>
              
              {/* Filter Toggle */}
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="border-white/20 text-[#A1A1AA] hover:text-white h-12"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? "rotate-180" : ""}`} />
              </Button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <Card className="mt-4 bg-[#0A0A0A] border-white/10">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Game Type */}
                    <div>
                      <label className="block text-[#A1A1AA] text-sm font-ubuntu mb-2">Game Type</label>
                      <div className="flex flex-wrap gap-2">
                        {GAME_TYPES.map((type) => (
                          <button
                            key={type}
                            onClick={() => setGameType(type)}
                            className={`px-3 py-1.5 rounded text-sm font-ubuntu transition-colors ${
                              gameType === type
                                ? "bg-[#7a6200] text-white"
                                : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                            }`}
                            data-testid={`filter-game-${type.toLowerCase()}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Skill Level */}
                    <div>
                      <label className="block text-[#A1A1AA] text-sm font-ubuntu mb-2">Skill Level</label>
                      <div className="flex flex-wrap gap-2">
                        {SKILL_LEVELS.map((level) => (
                          <button
                            key={level}
                            onClick={() => setSkillLevel(level)}
                            className={`px-3 py-1.5 rounded text-sm font-ubuntu transition-colors ${
                              skillLevel === level
                                ? "bg-[#7a6200] text-white"
                                : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                            }`}
                            data-testid={`filter-skill-${level.toLowerCase()}`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sort By */}
                    <div>
                      <label className="block text-[#A1A1AA] text-sm font-ubuntu mb-2">Sort By</label>
                      <div className="flex flex-wrap gap-2">
                        {SORT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setSortBy(option.value)}
                            className={`px-3 py-1.5 rounded text-sm font-ubuntu transition-colors flex items-center gap-1 ${
                              sortBy === option.value
                                ? "bg-[#7a6200] text-white"
                                : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                            }`}
                            data-testid={`sort-${option.value}`}
                          >
                            <option.icon className="w-3 h-3" />
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#7a6200] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Player Grid */}
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bebas text-xl tracking-wider text-[#7a6200]">
                    {gameType !== "All" || skillLevel !== "All" ? "FILTERED RESULTS" : "ALL PLAYERS"}
                  </h2>
                  <span className="text-[#52525B] text-sm font-ubuntu">
                    {filteredPlayers.length} player{filteredPlayers.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {filteredPlayers.length === 0 ? (
                  <div className="text-center py-12 bg-[#0A0A0A] rounded-lg border border-white/10">
                    <Users className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
                    <p className="text-[#A1A1AA] font-ubuntu">No players found matching your criteria</p>
                    <p className="text-[#52525B] text-sm font-ubuntu mt-2">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredPlayers.map((player, index) => (
                      <PlayerCard 
                        key={player.id} 
                        player={player}
                        showRank={sortBy === "rating" && index < 3}
                        rank={index + 1}
                        onViewProfile={handleViewProfile}
                        user={user}
                        token={token}
                        onFollowChange={fetchPlayers}
                        onChallenge={handleChallenge}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Top Rated Section */}
                <Card className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Trophy className="w-5 h-5 text-[#7a6200]" />
                      <h3 className="font-bebas text-lg tracking-wider text-white">TOP RATED</h3>
                    </div>
                    <div className="space-y-2">
                      {topRated.map((player, index) => (
                        <div key={player.id} className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bebas flex-shrink-0 ${
                            index === 0 ? "bg-yellow-500 text-black" :
                            index === 1 ? "bg-gray-300 text-black" :
                            index === 2 ? "bg-amber-700 text-white" :
                            "bg-white/10 text-white"
                          }`}>
                            {index + 1}
                          </span>
                          <SmallPlayerCard player={player} onViewProfile={handleViewProfile} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recently Active Section */}
                <Card className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-[#7a6200]" />
                      <h3 className="font-bebas text-lg tracking-wider text-white">RECENTLY ACTIVE</h3>
                    </div>
                    <div className="space-y-2">
                      {recentlyActive.map((player) => (
                        <SmallPlayerCard key={player.id} player={player} showActivity onViewProfile={handleViewProfile} />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card className="bg-[#0A0A0A] border-[#7a6200]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-[#C6A300] font-bebas text-3xl">{players.length}</p>
                    <p className="text-[#A1A1AA] font-ubuntu text-sm">Active Players</p>
                    <p className="text-[#52525B] text-xs font-ubuntu mt-2">
                      Join games and climb the leaderboard!
                    </p>
                    <Button 
                      onClick={() => navigate("/leaderboard")}
                      className="btn-gold w-full mt-4"
                      data-testid="view-leaderboard-btn"
                    >
                      View Leaderboard
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>

      {/* Challenge Modal */}
      <ChallengePlayerModal
        isOpen={challengeModalOpen}
        onClose={() => {
          setChallengeModalOpen(false);
          setSelectedOpponent(null);
        }}
        opponent={selectedOpponent}
        token={token}
      />
    </div>
  );
};

export default DiscoverPlayersPage;
